// Port of SuiteModel/EntityColors.swift (twelve OKLCH hues for story entities) and the app's colour-string helpers
// (`Color(scrivenerRGB:)`, `Color(entityRGB:)`): colours are stored as "r g b" with components 0…1.
import type { EntityKind } from './contract.ts';

export const ENTITY_HUES: readonly number[] = [35, 175, 285, 85, 225, 130, 335, 60, 255, 150, 310, 10];
export const ENTITY_LIGHTNESS = 0.56;
export const ENTITY_CHROMA = 0.12;

/** OKLCH to gamma-encoded sRGB, clamped to 0…1 (Björn Ottosson's matrices). */
export function oklchToSRGB(L: number, C: number, hue: number): [number, number, number] {
  const h = (hue * Math.PI) / 180;
  const a = C * Math.cos(h), b = C * Math.sin(h);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const linear = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
  const encode = (x: number) => { const v = Math.min(1, Math.max(0, x)); return v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055; };
  return [encode(linear[0]), encode(linear[1]), encode(linear[2])];
}

/** `EntityColors.string(hue:)`: "r g b", three decimals. */
export function entityColorString(hue: number): string {
  return oklchToSRGB(ENTITY_LIGHTNESS, ENTITY_CHROMA, hue).map((x) => x.toFixed(3)).join(' ');
}

/** `EntityColors.next(for:among:)`: the least-used hue among entities of the same kind (earliest on a tie). */
export function nextEntityColor(kind: EntityKind, others: { kind: EntityKind; color?: string }[]): string {
  const taken = others.filter((e) => e.kind === kind && e.color !== undefined).map((e) => e.color!);
  const uses = ENTITY_HUES.map((hue) => taken.filter((c) => c === entityColorString(hue)).length);
  const least = Math.min(...uses);
  return entityColorString(ENTITY_HUES[Math.max(0, uses.indexOf(least))]);
}

/** `Color(scrivenerRGB:)`: the components of an "r g b" string, or null when it has fewer than three numbers. */
export function parseRGB(rgb: string | null | undefined): [number, number, number] | null {
  if (!rgb) return null;
  const parts = rgb.split(' ').filter((p) => p !== '').map(Number).filter((n) => Number.isFinite(n));
  return parts.length >= 3 ? [parts[0], parts[1], parts[2]] : null;
}

/** An "r g b" colour as CSS `rgb(…)`, or null. */
export function rgbToCSS(rgb: string | null | undefined): string | null {
  const p = parseRGB(rgb);
  return p ? `rgb(${p.map((x) => Math.round(Math.min(1, Math.max(0, x)) * 255)).join(' ')})` : null;
}

/** `Color(entityRGB:)`: an entity's colour, or the first hue for one stored before colours, as CSS. */
export function entityCSSColor(rgb: string | null | undefined): string {
  return rgbToCSS(rgb) ?? rgbToCSS(entityColorString(ENTITY_HUES[0]))!;
}
